<script>
import UserIcon from "@/components/icons/UserIcon.vue";
import EmailIcon from "@/components/icons/EmailIcon.vue";
import PasswordIcon from "@/components/icons/PasswordIcon.vue";
import CreditCardIcon from "@/components/icons/CreditCardIcon.vue";

import Login from "@/components/login/Login.vue";
import AfterLogin from "@/components/login/AfterLogin.vue";
import ResetFeedbackMessage from "@/components/ResetFeedbackMessage.vue";

import { DateTime } from "luxon";

import { loadStripeSdk } from "vue-stripe-checkout/src/load-stripe-sdk";

import { validationMixin } from "vuelidate";

import "vue-select/src/scss/vue-select.scss";

import {
  required,
  requiredIf,
  minLength,
  email,
  sameAs,
} from "vuelidate/lib/validators";

import { BACKEND_URL, STRIPE } from "@/config.js";

export default {
  mixins: validationMixin,
  name: "RealRecipesCheckoutForm",
  page: {
    title: "RealRecipes - Advanced Aromatherapy Course",
    meta: [
      {
        name: "description",
        content: "RealRecipes - Advanced Aromatherapy Course",
      },
    ],
  },
  watch: {
    // call again the method if the route changes
    $route: "fetchData",
  },
  components: {
    UserIcon,
    CreditCardIcon,
    EmailIcon,
    PasswordIcon,
    Login,
    AfterLogin,
    ResetFeedbackMessage,
  },
  props: [
    "loadCombos",
    "guaranteeImg",
    "changeBus",
    "discount",
    "variant",
    "upsellCombo",
  ],
  computed: {
    hasSubscription: function () {
      let combo = this.selectedCombo;
      return combo && (combo.productComboId == 27 || combo.productComboId == 28);
    },
    validForm: function () {
      if (this.$v.form.$anyError) {
        return false;
      }
      return (
        this.cardNumberValid &&
        this.cardExpiryValid &&
        this.cardCVCValid &&
        this.cardPostalCodeValid
      );
    },
    validLoginForm: function () {
      return !this.$v.formLogin.$anyError;
    },
    validRecoveryForm: function () {
      return !this.$v.formRecoverPassword.$anyError;
    },
    installmentOptions: function () {
      let options = [];
      let combo = this.selectedCombo;
      if (!combo) {
        return options;
      }

      let currency = this.currencies.find(
        (c) => c.value === this.form.currency
      );
      if (!currency) {
        return options;
      }

      if (!combo.installmentOptions) {
        return options;
      }

      let iopt = combo.installmentOptions[this.form.currency];

      iopt.sort((o1, o2) => o1.installmentCount - o2.installmentCount);

      for (let opt of iopt) {
        var detail =
          opt.installmentCount == 1
            ? " one-time payment"
            : `1st invoice today plus 1 invoice per month for the next ${
                opt.installmentCount - 1
              } months. Total: ${this.formatPrice(
                opt.installmentCount * opt.installmentValue
              )}`;
        options.push({
          value: opt.installmentCount,
          text: `${opt.installmentCount}x ${this.formatPrice(
            opt.installmentValue
          )}`,
          detail: detail,
        });
      }

      return options;
    },
    subscriptionItem: function () {
      let combo = this.selectedCombo;
      if (combo) {
        return combo.products.find(p => p.category.categoryId == 1);
      }
      return null;
    },
    upsellItem: function () {
      if (!this.form.productComboId) {
        return null;
      }

      let comboWithUpsell = this.combos.find(
        (c) => c.productComboId == this.upsellCombo
      );
      if (!comboWithUpsell) {
        return null;
      }
      return comboWithUpsell.products.find((p) => p.id == 9);
    },
    selectedCombo: function () {
      if (!this.form.productComboId) {
        return null;
      }
      var combo = null;
      if (this.combos) {
        combo = this.combos.find(
          (c) => c.productComboId == this.form.productComboId
        );
      }
      return combo;
    },
    paymentErrorMessage: function () {
      if (!this.paymentError) {
        return null;
      }

      if (this.paymentError.code == "payment_intent_authentication_failure") {
        return "Your account is ready, but your card issuer requires a 3D Secure authentication. Please, Log In using the button on the top of this form and try again. Make sure to confirm the code your bank sent you (through SMS, email, mobile app, etc).";
      }

      if (this.paymentError.code != "card_declined") {
        return this.paymentError.message;
      }

      switch (this.paymentError.decline_code) {
        case "insufficient_funds":
          return "Your account was created and it is ready, but your payment was declined due to insufficient funds. Please, Log In using the button on the top of this form and try with a different card.";
        case "authentication_required":
          return "Your account was created and it is ready, but your card issuer requires a 3D Secure authentication. Please, Log In using the button on the top of this form and try again. Make sure to confirm the code your bank sent you (through SMS, email, mobile app, etc).";
        case "generic_decline":
        case "do_not_honor":
          return "Your account was created and it is ready, but your payment was declined by your card issuer. Please, contact your bank for more information, or Log In using the button on the top of this form and try again with a different card.";
        default:
          return "Your account was created and it is ready, but your payment was declined due to an unknown reason. Please, Log In using the button on the top of this form and try with a different card.";
      }
    },
  },

  data() {
    return {
      errors: {
        recoveryEmail: false,
        emailIsRegistered: false,
      },
      // after the user types in the email address, an attempt will be made
      // to fetch the account data already existing for that email. If an
      // existing account is found, this object will hold the details.
      existingCustomerInfo: {
        loaded: false,
        stripeId: null,
        firstName: null,
        lastName: null,
      },
      showLoad: false,
      emailIsRegistered: false,
      couponWarning: null,
      stripeKey: STRIPE.STRIPE_PUB_KEY,
      formDisabled: false,
      paymentComplete: false,
      paymentFailed: false,
      paymentError: null,
      selectedInstallments: 1,
      form: {
        firstName: null,
        lastName: null,
        email: null,
        password: null,
        couponCode: null,
        installmentCount: 1,
        productComboId: null,
        paymentMethod: "stripe",
        acceptTerms: false,
        acceptNewsletter: false,
        periodCount: 1,
        orderUUID: null,
        currency: "eur",
        includeUpsell: false,
        variant: this.variant,
      },
      formLogin: {
        email: null,
        password: null,
        loginAuthError: false,
      },
      formRecoverPassword: {
        email: null,
      },
      recoveryEmailWasSent: false,
      passwordRecoveryEmail: null,
      coupon: null,
      combos: [],
      currencies: [
        { value: "eur", text: "🇪🇺 EUR €", name: "Euro", symbol: "€" },
        { value: "usd", text: "🇺🇸 USD $", name: "US Dollar", symbol: "$" },
        {
          value: "brl",
          text: "🇧🇷 BRL R$",
          name: "Brazilian Real",
          symbol: "R$",
        },
        { value: "jpy", text: "🇯🇵 JPY ¥", name: "Yen", symbol: "¥" },
        { value: "gbp", text: "🇬🇧 GBP £", name: "British Pound", symbol: "£" },
        {
          value: "aud",
          text: "🇦🇺 AUD A$",
          name: "Australian Dollar",
          symbol: "A$",
        },
        { value: "cad", text: "🇨🇦 CAD $", name: "Canadian", symbol: "$" },
      ],
      stripe: null,
      cardNumberElement: null,
      cardNumberValid: false,
      cardExpiryElement: null,
      cardExpiryValid: false,
      cardCVCElement: null,
      cardCVCValid: false,
      cardPostalCodeElement: null,
      cardPostalCodeValid: false,
    };
  },
  created() {
    this.fetchData();
  },
  validations: {
    form: {
      firstName: {
        required,
        minLength: minLength(2),
      },
      lastName: {
        required,
        minLength: minLength(2),
      },
      email: {
        required,
        email,
      },
      password: {
        required: requiredIf(function (form) {
          form;
          let isRequired = !this.existingCustomerInfo.loaded;
          return isRequired;
        }),
        minLength: minLength(6),
      },
      acceptTerms: { sameAs: sameAs(() => true) },
    },
    formLogin: {
      email: {
        required,
        email,
      },
      password: {
        required,
        minLength: minLength(6),
      },
    },
    formRecoverPassword: {
      email: {
        required,
        email,
      },
    },
  },
  methods: {
    fetchData() {
      let ps = [];
      var pageCombos = this.loadCombos;

      pageCombos.forEach((id) => {
        let p = fetch(
          `${BACKEND_URL.Base}/products/combo/${id}?variant=${this.variant}`,
          {
            mode: "cors",
            headers: {
              Accept: "application/json",
            },
          }
        )
          .then((response) => {
            if (response.ok) {
              return response.json();
            }
          })
          .then((combo) => {
            let prices = new Map();
            combo.prices = prices;
            for (let product of combo.products) {
              let period = product.category.period;
              product.prices.forEach((price) => {
                var p = prices.get(price.currency);
                if (!p) {
                  let np = {};
                  Object.assign(np, price);
                  prices.set(price.currency, np);
                } else {
                  p.cents += price.cents;
                  p.vatCents += price.vatCents;
                  p.netCents += price.netCents;
                }
              });
              if (period === "Monthly") {
                combo.period = "Month";
              } else if (period === "Yearly") {
                combo.period = "Year";
              }
            }
            if (combo.products.find((p) => p.category.period === "OneTime")) {
              combo.period = "OneTime";
            }
            combo.value = combo.productComboId;
            window.combo = combo;
            return combo;
          })
          .catch((networkError) => {
            console.error(networkError);
          });
        ps.push(p);
      });

      Promise.all(ps).then((combos) => {
        this.combos = combos;
        console.log(JSON.stringify(combos, null, 2));
        let period = this.$route.query.period;
        if (this.combos.length > 1 && period) {
          let c = this.combos.find((c) =>
            c.products.find(
              (p) =>
                p.category.period.localeCompare(period, undefined, {
                  sensitivity: "accent",
                }) === 0
            )
          );
          this.form.productComboId = c
            ? c.productComboId
            : this.combos[0].productComboId;
        } else {
          this.form.productComboId = combos[0].productComboId;
        }

        let enabledCurrencies = this.currencies.filter((cur) => {
          let enabled = true;
          combos.forEach((combo) => {
            enabled = enabled && combo.prices.get(cur.value);
          });
          return enabled;
        });

        this.currencies = enabledCurrencies;

        let chosenCurrency = this.$route.query.currency;
        if (this.currencies.find((c) => c.value === chosenCurrency)) {
          this.form.currency = chosenCurrency;
        } else {
          this.form.currency = "eur";
        }

        loadStripeSdk("v3", () => {
          const options = {
            stripeAccount: this.stripeAccount,
            apiVersion: this.apiVersion,
            locale: this.locale,
          };
          if (!this.stripe) {
            this.stripe = window.Stripe(this.stripeKey, options);
            this.createAndMountStripeElements();
          }
        });
      });
    },
    toggleUpsell() {
      this.form.productComboId = this.form.includeUpsell
        ? this.upsellCombo[0]
        : this.loadCombos[0];
    },
    couponDiscountValue(combo) {
      if (this.coupon && this.form.couponCode) {
        switch (this.coupon.type) {
          case "CENTS":
            return this.coupon.moneyDiscountCents;
          case "PERCENT":
            return combo.price * (1.0 / this.coupon.percentDiscount);
        }
      }
      return 0.0;
    },
    createAndMountStripeElements() {
      const style = {
        base: {
          fontSize: "18px",
          color: "#495057",
          fontFamily: "Roboto Sans,sans-serif",
          "::placeholder": {
            color: "#aab7c4",
          },
        },
        invalid: {
          color: "#fa755a",
          iconColor: "#fa755a",
        },
      };

      try {
        var elements = this.stripe.elements();

        this.cardNumberElement = elements.create("cardNumber", {
          style: style,
          placeholder: "Card Number",
        });
        this.cardNumberElement.mount("#card-number-element");

        this.cardNumberElement.on(
          "change",
          this.errorDisplayFor("cardNumberValid", this.$refs.cardErrors)
        );

        this.cardExpiryElement = elements.create("cardExpiry", {
          style: style,
        });
        this.cardExpiryElement.mount("#card-expiry-element");
        this.cardExpiryElement.on(
          "change",
          this.errorDisplayFor("cardExpiryValid", this.$refs.cardExpiryErrors)
        );

        this.cardCvcElement = elements.create("cardCvc", {
          style: style,
        });
        this.cardCvcElement.mount("#card-cvc-element");
        this.cardCvcElement.on(
          "change",
          this.errorDisplayFor("cardCVCValid", this.$refs.cardCvcErrors)
        );

        this.cardPostalCodeElement = elements.create("postalCode", {
          style: style,
          placeholder: "Zip Code (from your billing address)",
        });
        this.cardPostalCodeElement.mount("#card-postalCode-element");
        this.cardPostalCodeElement.on(
          "change",
          this.errorDisplayFor(
            "cardPostalCodeValid",
            this.$refs.cardPostalCodeErrors
          )
        );
        console.log("Mounted stripe elements inputs");
      } catch (error) {
        console.error(error);
      }
    },
    errorDisplayFor(validity, elem) {
      let v = this;
      return (event) => {
        if (event.error) {
          elem.textContent = event.error.message;
        } else {
          elem.textContent = "";
        }
        v[validity] = event.complete;
      };
    },
    selectCurrency(currency) {
      this.form.currency = currency.value;
    },
    formatPrice(priceCents) {
      let price = (priceCents / 100).toFixed(2);
      let fmt = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: this.form.currency.toUpperCase(),
      });
      return fmt.format(price).replace(/(\.|,)00$/g, "");
    },
    subscriptionPrice() {
      let combo = this.selectedCombo;
      //let product = this.combos.find(c => c.productComboId === combo.productComboId);
      if (combo) {
        return this.showPrice(combo);
      }
      return "n.a.";
    },
    showDiscountValue(combo) {
      let value = this.couponDiscountValue(combo);
      return this.formatPrice(value);
    },
    comboPrice(combo) {
      if (!combo) {
        return "";
      }
      let discount = this.couponDiscountValue(combo);
      let installments = combo.installmentOptions[this.form.currency].find(
        (i) => i.installmentCount == this.selectedInstallments
      );
      var price = 0;
      if (installments) {
        price =
          installments.installmentCount * installments.installmentValue -
          discount;
      } else {
        price = combo.prices.get(this.form.currency).cents - discount;
      }
      let vat = Math.abs(price / 1.22 - price);
      return {
        cents: price,
        vatCents: vat,
        netCents: price - vat,
      };
    },
    showPrice(combo, excludeUpsell) {
      if (!combo) {
        return "";
      }
      let price = this.comboPrice(combo);
      if (this.form.includeUpsell && excludeUpsell) {
        let upsellPrice = this.upsellItem.prices.find(
          (p) => p.currency == this.form.currency
        );
        price.cents = price.cents - upsellPrice.cents;
      }
      if (combo.period === "Month" || combo.period === "Year") {
        return `${this.formatPrice(price.cents)} / ${combo.period}`;
      } else {
        return this.formatPrice(price.cents);
      }
    },
    showProductPrice(product) {
      if (!product) {
        return "";
      }
      let price = product.prices.find((p) => p.currency == this.form.currency);
      return `${this.formatPrice(price.cents)}`;
    },
    showVat(combo) {
      let price = this.comboPrice(combo);
      return this.formatPrice(price.vatCents);
    },
    showNetValue(combo) {
      let price = this.comboPrice(combo);
      return this.formatPrice(price.netCents);
    },
    nextRenewal() {
      let now = DateTime.utc();
      return `${now
        .plus({ months: 12 })
        .toLocaleString(DateTime.DATE_MED)} (Dropsmart subscription only)`;
    },

    validateInput(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },

    validateLogin(name) {
      const { $dirty, $error } = this.$v.formLogin[name];
      return $dirty ? !$error : null;
    },

    validateRecoveryEmail(name) {
      const { $dirty, $error } = this.$v.formRecoverPassword[name];
      return $dirty ? !$error : null;
    },

    removeCoupon() {
      this.coupon = null;
      this.form.couponCode = null;
    },

    fetchCoupon() {
      let couponUrl =
        BACKEND_URL.Coupon + "?couponCode=" + this.form.couponCode;
      fetch(couponUrl, {
        method: "GET",
        mode: "cors",
        headers: {
          Accept: "application/json",
        },
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else if (response.status == 404) {
            this.couponWarning = "Coupon not found";
            return null;
          } else if (response.status == 409) {
            this.couponWarning =
              "Coupon is not available for the selected subscription";
          }
        })
        .then((coupon) => {
          this.coupon = coupon;
        })
        .catch((error) => {
          console.error(error);
          return Promise.reject();
        });
    },
    afterCheckout(isUpgrade) {
      this.$router.push({
        name: "ThankYou",
        params: {
          isUpgrade: isUpgrade,
          isNextLevelCustomer: this.form.productComboId == 9,
          boughtOnlyAromaEmpress: false,
          hasSubscription: this.hasSubscription,
          nextAction: undefined,
        },
      });
    },
    submitCheckout() {
      console.log("SUBMITING THE CHECKOUT FORM");
      this.$v.form.$touch();
      if (!this.validForm) {
        return;
      }
      this.formDisabled = true;
      this.paymentComplete = false;
      this.paymentFailed = false;
      this.$bvToast.hide("paymentErrorBox");

      let customer = sessionStorage.getItem("customerInfo");
      var jwt = null;
      var isUpgrade = false;
      if (customer) {
        jwt = JSON.parse(customer).token.token;
        isUpgrade = true;
      }
      let reqHeaders = { "Content-type": "application/json" };
      if (jwt) {
        reqHeaders["Authorization"] = jwt;
      }

      this.form.installmentCount = this.selectedInstallments;

      let jsonForm = JSON.stringify(this.form);

      fetch(BACKEND_URL.Checkout, {
        method: "POST",
        mode: "cors",
        body: jsonForm,
        headers: reqHeaders,
        credentials: "include",
        cache: "no-cache",
      })
        .then((response) => {
          if (response.ok) {
            return response
              .json()
              .then((jsonData) => ({ status: "ok", data: jsonData }));
          } else {
            if (response.status == 409) {
              this.errors.emailIsRegistered = true;
              //Conflict, checkout submitted without logging in first and using an email which is already associated with an account.
              return {
                status: "error",
                data: {
                  code: "email_conflict",
                  message:
                    "The email address is already associated with an account. Please use the 'Log in' link above before completing your checkout.",
                },
              };
            } else {
              return response.text().then((textData) => ({
                status: "error",
                data: {
                  code: "generic_error",
                  message: textData,
                },
              }));
            }
          }
        })
        .then((result) => {
          if (result.status == "error") {
            this.paymentFailed = true;
            this.paymentComplete = true;
            this.paymentError = result.data;
            this.$bvToast.show("paymentErrorBox");
          } else {
            let jsonData = result.data;
            this.form.orderUUID = jsonData["order"];
            sessionStorage.setItem(
              "customerInfo",
              JSON.stringify(jsonData["customerInfo"])
            );

            let v = this;
            this.stripe
              .confirmCardPayment(jsonData["stripeSecret"], {
                payment_method: {
                  card: this.cardNumberElement,
                },
                billing_details: {},
              })
              .then(function (result) {
                if (result.error) {
                  v.paymentError = result.error;
                  v.paymentFailed = true;
                  v.$bvToast.show("paymentErrorBox");
                  console.log(
                    "Payment failed with error: " +
                      " code: " +
                      result.error.code +
                      ", decline_code: " +
                      result.error.decline_code +
                      ", message: " +
                      result.error.message +
                      ", params: " +
                      result.error.param
                  );
                } else {
                  v.afterCheckout(isUpgrade);
                }
                v.formDisabled = false;
                v.paymentComplete = true;
              });
          }
        })
        .catch((e) => {
          e;
          this.formDisabled = false;
        });
    },
    submitLogin() {
      this.$v.formLogin.$touch();
      if (!this.validLoginForm) {
        return;
      }

      let modal = this.$refs.modalLogin;

      this.showLoad = true;
      this.formLogin.loginAuthError = false;

      fetch(BACKEND_URL.Login, {
        method: "POST",
        body: JSON.stringify(this.formLogin),
        headers: {
          "Content-type": "application/json",
        },
        credentials: "same-origin",
        cache: "no-cache",
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            if (response.status == 401) {
              this.formLogin.loginAuthError = true;
            }
            throw Error(response.status + ": " + response.statusText);
          }
        })
        .then((loginResponse) => {
          this.showLoad = false;
          this.emailIsRegistered = false;
          this.formDisabled = false;
          sessionStorage.setItem("customerInfo", JSON.stringify(loginResponse));
          this.existingCustomerInfo.loaded = true;
          this.existingCustomerInfo.stripeId = loginResponse.user.stripeId;
          this.existingCustomerInfo.firstName = loginResponse.user.firstName;
          this.existingCustomerInfo.lastName = loginResponse.user.lastName;

          this.form.firstName = loginResponse.user.firstName;
          this.form.lastName = loginResponse.user.lastName;
          this.form.email = loginResponse.user.email;
          modal.hide();
        })
        .catch((error) => {
          this.showLoad = false;
          console.error(error);
        });
    },
    resetPasswordRecovery() {
      this.recoveryEmailWasSent = false;
      this.formRecoverPassword.email = null;
      this.errors.recoveryEmail = null;
    },
    submitRecoverPassword() {
      this.$v.formRecoverPassword.$touch();
      if (!this.validRecoveryForm) {
        return;
      }

      let email = this.formRecoverPassword.email;
      let url = BACKEND_URL.RecoverPasswordInfo + "?email=" + email;
      this.showLoad = true;
      fetch(url, {
        method: "POST",
        mode: "cors",
      })
        .then((response) => {
          if (response.ok) {
            this.recoveryEmailWasSent = true;
            this.passwordRecoveryEmail = email;
            this.$refs.modalRecoverPassword.hide();
            this.$refs.modalRecoverPasswordFeedback.show();
          } else if (response.status == 404) {
            this.showLoad = false;
            this.recoveryEmailWasSent = false;
            this.errors.recoveryEmail = true;
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    checkEmail() {
      if (!this.form.email) {
        return;
      }

      let customer = sessionStorage.getItem("customerInfo");
      if (customer) {
        let customerInfo = JSON.parse(customer);
        if (this.form.email === customerInfo.user.email) {
          this.emailIsRegistered = false;
          this.formDisabled = false;
          this.paymentError = null;
          return;
        }
      }

      let url = BACKEND_URL.CheckEmail + "?email=" + this.form.email;
      fetch(url, {
        method: "GET",
        mode: "cors",
      })
        .then((response) => {
          if (response.ok || response.status == 204) {
            this.emailIsRegistered = response.status != 204;
          } else {
            throw Error(response.status + " " + response.statusText);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    logoff() {
      this.existingCustomerInfo.loaded = false;
      this.existingCustomerInfo.firstName = null;
      this.existingCustomerInfo.lastName = null;
      this.existingCustomerInfo.email = null;
      this.existingCustomerInfo.stripeId = null;
      this.emailIsRegistered = false;
      this.form.firstName = null;
      this.form.lastName = null;
      this.form.email = null;
      this.form.password = null;
      this.$v.$reset();
      sessionStorage.removeItem("customerInfo");
    },
    say: function (message) {
      alert(message);
    },
  },
  mounted() {
    this.changeBus.$on("start-filling-form", () =>
      this.$refs["firstNameInput"].focus()
    );

    this.$root.$on("bv::modal::show", (bvEvent, modalId) => {
      bvEvent, modalId;
      this.resetPasswordRecovery();
    });
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      bvEvent, modalId;
      this.resetPasswordRecovery();
    });
    this.logoff();
  },
};
</script>

<template>
  <div class="boxCheckout">
    <Login v-if="!this.existingCustomerInfo.loaded" />

    <b-modal
      class="dsModal"
      id="modalRecoverPassword"
      ref="modalRecoverPassword"
      :hide-footer="true"
      centered
      ok-only
      hide-header
    >
      <template v-slot:default>
        <h2>Lost your password?</h2>
        <p>
          Enter your email address and we'll send you a link to create a new
          password.
        </p>
        <b-form
          style="text-align: left"
          @submit.prevent="submitRecoverPassword"
          ref="recoverPasswordForm"
          aria-autocomplete="off"
        >
          <!-- E-mail Input -->
          <b-input-group class>
            <span class="formIcons email">
              <EmailIcon />
            </span>
            <b-form-input
              v-model="$v.formRecoverPassword.email.$model"
              @keypress="errors.recoveryEmail = false"
              type="email"
              name="email"
              class="LoginInput"
              size="lg"
              placeholder="E-MAIL"
              :state="validateRecoveryEmail('email')"
              aria-describedby="email-feedback"
            ></b-form-input>
            <b-form-invalid-feedback id="email-feedback" v-bind:tooltip="false"
              >Must be a valid email address.</b-form-invalid-feedback
            >
          </b-input-group>
          <b-alert variant="error" v-model="errors.recoveryEmail"
            >This email is not associated with any account</b-alert
          >
          <button class="button-login" type="submit" id="submitRecoverPassword">
            <b-spinner v-show="showLoad"></b-spinner>
            <span id="button-text" v-if="!showLoad">Reset password</span>
          </button>
          <a
            href="https://dropsmarteo.com/support"
            target="_blank"
            class="support"
            >Still having problems? Click here for more information.</a
          >
        </b-form>
      </template>
    </b-modal>

    <b-modal
      class="dsModal"
      id="modalLogin"
      ref="modalLogin"
      centered
      ok-only
      no-stacking
      hide-header
    >
      <template v-slot:default>
        <h2>Log in to DROPSMART</h2>
        <b-form
          style="text-align: left"
          @submit.prevent="submitLogin"
          ref="loginForm"
          aria-autocomplete="off"
        >
          <!-- E-mail Input -->
          <b-input-group class>
            <span class="formIcons email">
              <EmailIcon />
            </span>
            <b-form-input
              v-model="$v.formLogin.email.$model"
              type="email"
              name="email"
              class="LoginInput"
              size="lg"
              placeholder="E-MAIL"
              :state="validateLogin('email')"
              aria-describedby="email-feedback"
            ></b-form-input>
            <b-form-invalid-feedback id="email-feedback" v-bind:tooltip="false"
              >Must be a valid email address.</b-form-invalid-feedback
            >
          </b-input-group>
          <!-- Password Input -->
          <b-input-group class>
            <span class="formIcons password">
              <PasswordIcon />
            </span>
            <b-form-input
              class="LoginInput"
              size="lg"
              v-model="$v.formLogin.password.$model"
              type="password"
              name="password"
              placeholder="PASSWORD"
              :state="validateLogin('password')"
              aria-describedby="password-feedback"
            ></b-form-input>
            <b-form-invalid-feedback
              id="password-feedback"
              v-bind:tooltip="false"
            >
              Your password must be at least 6 characters.
            </b-form-invalid-feedback>
          </b-input-group>
          <b-alert variant="error" :show="formLogin.loginAuthError"
            >Invalid email or password</b-alert
          >
          <button class="button-login" type="submit" id="submitLogin">
            <b-spinner v-show="showLoad"></b-spinner>
            <span class id="button-text" v-if="!showLoad">LOG IN</span>
          </button>
        </b-form>
      </template>

      <template v-slot:modal-footer>
        <a href="#" class="forgot-password" v-b-modal.modalRecoverPassword
          >Forgot your password?</a
        >
      </template>
    </b-modal>

    <b-modal
      class="dsModal"
      id="modalRecoverPasswordFeedback"
      ref="modalRecoverPasswordFeedback"
      :hide-footer="true"
      :hide-header="true"
      centered
      ok-only
    >
      <ResetFeedbackMessage :customerEmail="passwordRecoveryEmail" />
    </b-modal>

    <AfterLogin
      v-if="this.existingCustomerInfo.loaded"
      v-bind:user="existingCustomerInfo"
      v-on:logoff="logoff"
    />
    <b-form
      style="text-align: left"
      @submit.prevent="submitCheckout"
      ref="checkoutForm"
    >
      <h2 class="form-header">Sign Up</h2>
      <!-- First Name Input -->
      <b-input-group class>
        <span class="formIcons">
          <UserIcon />
        </span>
        <b-form-input
          id="firstNameInput"
          ref="firstNameInput"
          v-model="$v.form.firstName.$model"
          name="firstName"
          class="LoginInput"
          size="lg"
          placeholder="FIRST NAME"
          :state="validateInput('firstName')"
          aria-describedby="firstName-feedback"
        ></b-form-input>
        <b-form-invalid-feedback id="firstName-feedback" v-bind:tooltip="false"
          >This is a required field.</b-form-invalid-feedback
        >
      </b-input-group>

      <!-- Last Name Input -->
      <b-input-group class>
        <span class="formIcons">
          <UserIcon />
        </span>
        <b-form-input
          v-model="$v.form.lastName.$model"
          name="lastName"
          class="LoginInput"
          size="lg"
          placeholder="LAST NAME"
          :state="validateInput('lastName')"
          aria-describedby="lastName-feedback"
        ></b-form-input>
        <b-form-invalid-feedback id="lastName-feedback" v-bind:tooltip="false"
          >This is a required field.</b-form-invalid-feedback
        >
      </b-input-group>
      <!-- E-mail Input -->
      <b-input-group class>
        <span class="formIcons email">
          <EmailIcon />
        </span>
        <b-form-input
          v-model="$v.form.email.$model"
          type="email"
          name="email"
          class="LoginInput"
          size="lg"
          placeholder="E-MAIL"
          autocomplete="new-password"
          :state="validateInput('email')"
          aria-describedby="email-feedback"
          @blur="checkEmail()"
        ></b-form-input>
        <div
          v-show="this.emailIsRegistered"
          class="invalid-feedback-custom email-exist"
        >
          This email is already linked with a Dropsmart account. <br />
          Please <a href="#" v-b-modal.modalLogin>sign in here</a>
        </div>
        <b-form-invalid-feedback id="email-feedback" v-bind:tooltip="false"
          >Must be a valid email address.</b-form-invalid-feedback
        >
      </b-input-group>

      <!-- Password Input -->
      <b-input-group v-show="!this.existingCustomerInfo.loaded" class>
        <span class="formIcons password">
          <PasswordIcon />
        </span>
        <b-form-input
          class="LoginInput"
          size="lg"
          v-model="$v.form.password.$model"
          type="password"
          name="password"
          autocomplete="new-password"
          placeholder="CREATE YOUR PASSWORD"
          :state="validateInput('password')"
          aria-describedby="password-feedback"
        ></b-form-input>
        <b-form-invalid-feedback id="password-feedback" v-bind:tooltip="false">
          Your password must be at least 6 characters.
        </b-form-invalid-feedback>
      </b-input-group>

      <b-card
        no-body
        no-title
        style="border: 1px solid red; border-radius: 4px"
        class="px-0"
        no-gutter
      >
        <b-card-text v-if="selectedCombo" class="px-0">
          <b-row class="mx-2 mb-2">
            <b-col
              class="col-8 text-left"
              style="text-transform: uppercase; font-weight: 700"
            >
              order summary
            </b-col>
            <b-col class="col-4 justify-content-end">
              <b-form-select
                v-model="form.currency"
                size="sm"
                :options="currencies"
                class="currency-selector"
              >
              </b-form-select>
            </b-col>
          </b-row>
          <b-row class="text-muted medium mx-2">
            <b-col class="text-left col-8" style="font-size: 0.9em">
              {{ "Speed Start" }}
            </b-col>
            <b-col class="text-right col-4">
              {{ showPrice(selectedCombo, true) }}
            </b-col>
          </b-row>
          <b-row class="text-muted medium mx-2" v-if="hasSubscription">
            <b-col class="text-left col-8" style="font-size: 0.9em">
              {{ "1st Year of Dropsmart" }}
            </b-col>
            <b-col class="text-right col-4">
              <s>{{ showProductPrice(subscriptionItem) }}</s>
            </b-col>
          </b-row>
          <b-row v-if="form.includeUpsell" class="text-muted medium mx-2">
            <b-col class="text-left col-8" style="font-size: 0.9em">
              {{ upsellItem.name }}
            </b-col>
            <b-col class="text-right col-4">
              {{ showProductPrice(upsellItem) }}
            </b-col>
          </b-row>
          <b-row
            style="background-color: #eee; height: 3rem"
            class="align-items-center mx-0 mt-2 align-top"
          >
            <b-col class="justify-content-end text-right">
              <div class="align-top">
                <span
                  style="vertical-align: middle; font-size: 0.65em"
                  class="mx-3 pb-2"
                  >Total</span
                >
                <span
                  style="
                    font-size: 1.2em;
                    font-weight: 700;
                    vertical-align: top;
                  "
                  class="pt-2"
                  >{{ showPrice(selectedCombo, false) }}</span
                >
              </div>
            </b-col>
          </b-row>

          <b-row>
            <b-col class="text-center pt-2">
              <div class="first-payment-date" v-if="hasSubscription">
                First renewal: {{ nextRenewal() }}
              </div>
              <div class="order-total" style="display: none">
                Order total includes VAT.
                <span class="vat-text">
                  <span class="underline">See details</span>
                  <div class="vat-details">
                    <div class="itens">
                      <p>
                        Items:
                        <span>{{ showNetValue(this.selectedCombo) }}</span>
                      </p>
                    </div>
                    <div class="vat">
                      <p>
                        VAT:
                        <span>{{ showVat(this.selectedCombo) }}</span>
                      </p>
                    </div>
                    <div v-if="this.coupon">
                      <p>
                        Discount:
                        <span>
                          {{ showDiscountValue(this.selectedCombo) }}
                        </span>
                      </p>
                    </div>
                    <div class="total">
                      <p>
                        Order Total
                        <span>{{ showPrice(this.selectedCombo) }}</span>
                      </p>
                    </div>
                  </div>
                </span>
              </div>
            </b-col>
          </b-row>
        </b-card-text>
      </b-card>

      <b-card
        no-body
        no-title
        id="upsell-card"
        class="text-left"
        v-if="upsellItem"
      >
        <b-card-text>
          <b-form-group class="checkout-checkbox pl-0" style="padding: 1px 1px; margin-bottom: 10px; background-color: rgb(255, 255, 153); text-align: center; font-size: 0.8em;">
            <img
              src="@/assets/images/arrow-flash-small.gif"
              alt=""
              style="
                max-width: 100%;
                height: auto;
                vertical-align: middle;
                border: 0px;
                display: inline;
              "
              data-cf-id="flashing-arrow"
              data-cf-note="flashing arrow"
              data-cf-editable-type="image"
            />
            <b-form-checkbox
              class="align-top pt-1"
              style="display: inline; margin-left: 0.5em;"
              id="includeUpsell"
              v-model="form.includeUpsell"
              name="includeUpsell"
              @change="toggleUpsell()"
              ><div
                style="
                  color: #e00b11;
                  margin-top: -0px;
                  font-weight: 700;
                  font-size: 1.1em;
                "
              >{{ upsellItem.name }} (+
                {{ showProductPrice(upsellItem) }})</div>
            </b-form-checkbox>
          </b-form-group>
          <p>
            <strong>MARK THE CHECKBOX ABOVE</strong> to add Formulating Foundations to your order. Setting fresh standards for blending with essential oils in one day class. Massive amount of professional, straightforward formulating strategies in minutes, ready to implement practice.
          </p>
          <p>
            <ul>
              <li>Certificate of Attendance.</li>
              <li>Lifetime Access.</li>
              <li>Plus 5 Extra BONUSES you will get:</li>
              <ul>
                <li>Basic chemistry PDF.</li>
                <li>VIP Community.</li>
                <li>3 extra lessons FOCUS ON YOU.</li>
              </ul>
            </ul>
          </p>
          <p>
            Mark the checkbox and secure your spot for this excellent price NOW.
          </p>
        </b-card-text>
      </b-card>

      <h2 class="form-header">Payment</h2>
      <img
        class="img-payment"
        src="@/assets/images/checkout-credit-cards.png"
      />
      <b-input-group class="paymentInput" label="Debit/Credit">
        <b-input-group-prepend is-text>
          <span class="card-label">
            <CreditCardIcon />
          </span>
        </b-input-group-prepend>
        <div
          id="card-number-element"
          class="form-control stripe-element-container"
        >
          <!-- Stripe Card Element -->
        </div>
      </b-input-group>
      <div ref="cardErrors" role="alert" class="sr-field-error"></div>

      <div class="twoColumn">
        <!-- Payment Validate Input -->
        <div>
          <b-input-group class="paymentInput">
            <b-input-group-prepend is-text>
              <span class="card-label">
                <CreditCardIcon />
              </span>
            </b-input-group-prepend>
            <div
              id="card-expiry-element"
              class="form-control stripe-element-container"
            >
              <!-- Stripe Card Expiry Element -->
            </div>
          </b-input-group>
          <div ref="cardExpiryErrors" role="alert" class="sr-field-error"></div>
        </div>
        <div>
          <!-- Payment CVC Input -->
          <b-input-group class="paymentInput">
            <b-input-group-prepend is-text>
              <span class="card-label">
                <CreditCardIcon />
              </span>
            </b-input-group-prepend>
            <div
              id="card-cvc-element"
              class="form-control stripe-element-container"
            >
              <!-- Stripe CVC Element -->
            </div>
          </b-input-group>
          <div ref="cardCvcErrors" role="alert" class="sr-field-error"></div>
        </div>
      </div>

      <b-input-group class="paymentInput">
        <b-input-group-prepend is-text>
          <span class="card-label">
            <CreditCardIcon />
          </span>
        </b-input-group-prepend>
        <div
          id="card-postalCode-element"
          class="form-control stripe-element-container"
        >
          <!-- Stripe Postal Code Element -->
        </div>
      </b-input-group>
      <div ref="cardPostalCodeErrors" role="alert" class="sr-field-error"></div>

      <b-input-group class="paymentInput" v-if="installmentOptions.length > 1">
        <b-input-group-prepend is-text>
          <span class="card-label"> <CreditCardIcon /></span>
        </b-input-group-prepend>
        <v-select
          :searchable="false"
          :clearable="false"
          :options="installmentOptions"
          v-model="selectedInstallments"
          :key="selectedInstallments"
          :reduce="(opt) => opt.value"
          inputId="selectInstallmentInput"
          label="text"
          class="installments"
          placeholder="Choose an installment plan"
        >
          <template v-slot:option="option">
            <div style="font-weight: bold">{{ option.text }}</div>
            <div style="white-space: pre-wrap; font-size: 0.8rem">
              {{ option.detail }}
            </div>
          </template>
        </v-select>
      </b-input-group>
      <!-- Image Secure SSL Encryption -->
      <img
        class="secure-ssl"
        :src="require('@/assets/images/' + this.guaranteeImg)"
        alt="Secure SSL Encryption"
      />

      <!-- Terms and Conditions accept -->
      <b-form-group class="checkout-checkbox">
        <b-form-checkbox
          id="acceptTerms"
          v-model="$v.form.acceptTerms.$model"
          name="acceptTerms"
          :state="validateInput('acceptTerms')"
          aria-describedby="terms-conditions-feedback"
        >
          I agree to these
          <a
            target="_blank"
            href="https://dropsmarteo.com/terms-and-conditions/"
            >terms and conditions</a
          >
        </b-form-checkbox>
        <b-form-invalid-feedback
          id="terms-conditions-feedback"
          v-bind:tooltip="true"
        >
          You must agree to the Terms and Conditions in order to complete the
          checkout process.
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Newsletter accept -->
      <b-form-group class="checkout-checkbox">
        <b-form-checkbox
          id="acceptNewsletter"
          v-model="form.acceptNewsletter"
          name="acceptNewsletter"
        >
          I want to stay informed by Dropsmart's newsletter
          <small>(we send content related to your subscription by email)</small>
        </b-form-checkbox>
      </b-form-group>

      <div class="textGDPR">
        <p>
          We respect the GDPR rules. Your privacy is important to us. We don't
          send spam, and you can unsubscribe at any time.
        </p>
      </div>

      <b-toast
        id="paymentErrorBox"
        variant="danger"
        static
        solid
        no-auto-hide
        no-close-button
      >
        {{ paymentErrorMessage }}
      </b-toast>
      <div class ref="checkoutButtonWrapper">
        <button
          class="button-checkout"
          type="submit"
          id="submitButton"
          :disabled="formDisabled"
        >
          <b-spinner
            v-show="formDisabled && !paymentComplete"
            id="spinner"
            label="Processing payment"
          ></b-spinner>
          <span id="button-text" v-show="paymentComplete || !formDisabled"
            >Give me instant access!</span
          >
        </button>
        <b-popover
          target="submitButton"
          triggers="click blur"
          :disabled="validForm"
          placement="top"
        >
          <template v-slot:title>Incomplete information</template>
          <span style="color: tomato; font-weight: bold">
            The information provided is incomplete. Please check all fields on
            this form, and make sure you have agreed to our Terms and
            Conditions.
          </span>
        </b-popover>
      </div>

      <b-container class="support" style="text-align: center">
        <b-row>
          <b-col>
            <div style="margin: 40px 0 0 0">
              <b>Do you have a question? Contact us.</b>
              <p>
                <a href="mailto:support@dropsmarteo.com">support@dropsmarteo.com</a>
              </p>
            </div>
          </b-col>
        </b-row>
      </b-container>
      <!-- </b-form-group> -->
    </b-form>
  </div>
</template>

<style lang="scss">
$vs-state-active-bg: #dddbdb;
$vs-state-active-color: #333;
@import "vue-select/src/scss/vue-select.scss";
@font-face {
  font-family: OpenSans;
  src: url(../../../assets/fonts/OpenSans-Light.ttf);
  font-weight: 200;
}

@font-face {
  font-family: OpenSans;
  src: url(../../../assets/fonts/OpenSans-Regular.ttf);
  font-weight: 400;
}

@font-face {
  font-family: OpenSans;
  src: url(../../../assets/fonts/OpenSans-Bold.ttf);
  font-weight: 600;
}

// Global Settings
$font-family: "OpenSans";
$font-bold: 700;

// Form Settings
$border-input: 1px solid #f7f7f7;
$bg-input: #f7f7f7;
$color-input: #000000;
$font-size-input: 18px;
$color: #000000;
$line-height-input: 23px;
$padding-input: 15px 18px 15px 55px;
$link-color: #68a432;

// Icon Settings
$icon-color: #999999;

* {
  font-family: $font-family;
}

.alert-error {
  color: #ff0000;
  font-size: 16px;
  margin: 0 !important;
  padding: 0 !important;
  text-align: center;
}

.boxCheckout {
  background-color: #ffffff;
  border: 1px solid #aaaaaa;
  padding: 60px 15px;
  text-align: left;
  .box-login,
  .after-login {
    margin: 0 auto 30px;
    max-width: 415px;
  }
}

form {
  font-family: $font-family;
  font-size: $font-size-input;
  margin: auto;
  max-width: 415px;
  width: 100%;
  .input-group {
    margin: 0 0 8px 0;
    .formIcons {
      position: absolute;
      top: 1px;
      left: 1px;
      width: 57px;
      line-height: 53px;
      z-index: 100;
      svg {
        font-size: 16px;
        position: relative;
        z-index: 100;
        width: 57px;
        path {
          fill: $icon-color;
        }
      }
      &.email,
      &.password,
      &.coupon,
      &.creditcard {
        svg {
          font-size: 20px;
        }
      }
    }
    input {
      background: $bg-input;
      background-image: none !important;
      border: $border-input;
      border-radius: 6px !important;
      color: $color-input;
      font-size: $font-size-input;
      height: auto;
      line-height: $line-height-input;
      padding: $padding-input;
      &:focus {
        background-color: #ffffff;
        border: 1px solid #dcdcdc;
      }
    }
    #selectInstallmentInput {
      background-color: $bg-input;
      border: none;
      padding: 0;
      &:active,
      &:hover,
      &:focus,
      &:selected {
        background-color: $bg-input;
        color: $color-input;
      }
    }

    .installments {
      height: auto;
      width: 80%;
      margin: auto;
      line-height: $line-height-input;
      border: $border-input;
      border-radius: 6px !important;
    }

    .installments .vs__dropdown-toggle,
    .installments .vs__dropdown-menu {
      background: $bg-input;
      border: none;
      color: $color-input;
      li,
      span,
      div {
        &:checked {
          background-color: #fafafa;
          border: 1px solid #dcdcdc;
          color: #444;
        }
      }
    }

    .installments .vs__clear {
      display: none;
    }
    .installments .vs__open-indicator {
      fill: #394066;
    }

    .form-control {
      &:focus,
      .is-invalid,
      .is-valid {
        box-shadow: none;
      }
    }
    .invalid-feedback-custom {
      color: darkred;
    }
    .email-exist {
      margin-top: 0.25rem;
      font-size: 80%;
      color: #dc3545;
      a {
        color: #509610;
      }
    }
    .discount-apply {
      align-self: center;
      color: $color;
      cursor: pointer;
      font-size: 14px;
      font-weight: $font-bold;
      line-height: 36px;
      position: absolute;
      right: 35px;
      z-index: 99;
    }
    .discount-price {
      align-self: center;
      color: $color;
      font-weight: $font-bold;
      position: absolute;
      right: 0;
      .remove-coupon {
        cursor: pointer;
        display: inline-block;
        font-size: 24px;
        padding: 0px 11px;
        transform: rotate(45deg);
        -ms-transform: rotate(45deg);
      }
    }
  }
}
form {
  .radio-products {
    display: flex;
    flex-direction: column;
    margin: 30px 0 10px;
    padding: 0 15px;
    width: 100%;
    /*label.custom-control-label[for="__BVID__27_BV_option_1"] > span:after {
      display: block;
      content: "Recommended - 14% Off";
      color: #509610;
      font-size: 14px;
      width: 100%;
    }*/
    .custom-control {
      padding-left: 35px;
    }
    input {
      &:focus ~ label {
        &::before {
          box-shadow: none !important;
        }
      }
      &:checked ~ label {
        &::after {
          top: 9px;
          left: -29px;
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #ffffff !important;
        }
        &::before {
          background: $link-color !important;
        }
      }
    }
    label {
      cursor: pointer;
      margin-bottom: 12px;
      width: 100%;
      text-align: left;
      &:hover {
        &::before,
        &::after {
          background-color: #949494 !important;
        }
      }
      &::before,
      &::after {
        background-color: #d9d9d9 !important;
        border: 0;
        border-radius: 50%;
        height: 21px;
        left: -35px;
        top: 3px;
        width: 21px;
      }
      .price {
        font-weight: $font-bold;
        float: right;
      }
      .recommended {
        color: #79b867;
        font-size: 0.8em;
        font-weight: $font-bold;
      }
    }
  }
}
form {
  .card {
    background-color: #ffffff;
    border: 2px solid #e6e6e6;
    border-radius: 0;
    margin: 11px 0 50px 0;
    padding: 18px;
    text-align: center;
    width: 100%;
    .card-title {
      color: $color;
      font-size: 24px;
      font-weight: 700;
      line-height: 40px;
      margin: 0;
    }
    .first-payment-date {
      font-size: 12px;
      line-height: 24px;
    }
    .order-total {
      color: $color;
      font-size: 14px;
      margin: 0;
      .vat-text:hover {
        .vat-details {
          display: block;
          position: absolute;
          right: -150px;
        }
      }
      .underline {
        color: $link-color;
        cursor: pointer;
        &:hover {
          text-decoration: underline;
        }
      }
    }
    .vat-details {
      background: #ffffff;
      border: 1px solid #cbcbcb;
      display: none;
      max-width: 280px;
      padding: 15px 20px 10px;
      text-decoration: none;
      width: 100%;
      z-index: 99;
      p {
        color: $color;
        font-size: 16px;
        line-height: 30px;
        margin: 0;
        padding: 0 10px;
        text-align: left;
        width: 100%;
        &:hover {
          text-decoration: none;
        }
        span {
          color: $color;
          float: right;
        }
      }
      .total {
        border-top: 1px solid #cbcbcb;
        margin-top: 5px;
        p {
          color: $link-color;
          font-weight: 700;
          line-height: 40px;
          span {
            color: $link-color;
          }
        }
      }
    }
  }
}
form {
  img.payments,
  img.secure-ssl {
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 80%;
  }
  img.secure-ssl {
    padding: 25px 0;
    &:after {
      content: "";
      border-bottom: 1px solid #cbcbcb;
    }
  }
  .hide-radio-payments {
    padding: 0;
    label {
      &:before,
      &:after {
        display: none;
      }
    }
  }
}

.img-payment {
  margin-bottom: 20px;
  max-width: 254px;
  width: 100%;
}

.paymentInput {
  background: #f7f7f7;
  border: 1px solid #f7f7f7;
  border-radius: 6px;
  .StripeElement--focus {
    outline: none;
    box-shadow: none;
  }
  &.input-group {
    svg {
      font-size: 20px;
      path {
        fill: #999999;
      }
    }
  }
  .input-group-text {
    background: transparent;
    border: 0;
    line-height: 41px;
    width: 57px;
  }
  .form-control {
    background: #f7f7f7 !important;
    border: 0;
    height: auto;
    line-height: 41px;
    align-self: center;
  }
  .card-label {
    text-align: center;
    width: 100%;
  }
}

//STRIPE ELEMENTS
.stripe-element-container {
  align-items: center;
  background: #f7f7f7 !important;
  display: grid;
}
.StripeElement--focus {
  box-shadow: 0;
}

//MODAL
#modalLogin,
#modalRecoverPassword {
  .modal-dialog {
    max-width: 575px;
    .modal-content {
      border: 0;
      border-radius: 0;
      h2 {
        color: #000000;
        font-size: 36px;
        font-weight: 700;
        margin-bottom: 35px;
        padding-top: 60px;
        text-align: center;
        text-transform: uppercase;
        width: 100%;
      }
      p {
        color: #000000;
        max-width: 360px;
        margin: 0 auto 25px;
        text-align: center;
        font-size: 18px;
      }
      .modal-header {
        border: 0;
        padding-top: 15px;
        flex-direction: column;
        p {
          color: #000000;
          font-size: 18px;
          margin: 0 auto;
          max-width: 360px;
          padding-top: 15px;
          text-align: center;
        }
      }
      .modal-footer {
        border: 0;
        padding-top: 0;
        padding-bottom: 65px;
        text-align: center;
      }
      .button-login {
        background: #79b867;
        border: 1px solid #79b867;
        border-radius: 50px;
        color: #ffffff;
        font-size: 20px;
        font-weight: 700;
        line-height: 53px;
        margin-top: 35px;
        padding: 0 15px;
        text-transform: uppercase;
        width: 100%;
        .spinner-border {
          vertical-align: middle;
        }
        &:hover {
          background: #000000;
          border-color: #000000;
          color: #ffffff;
        }
        &:focus {
          outline: none;
        }
      }
      .forgot-password {
        color: #666666;
        font-size: 13px;
        width: 100%;
      }
      .support {
        font-size: 14px;
        text-align: center;
        display: block;
        margin: 35px 0 30px;
        &:hover {
          color: #79b867;
          text-decoration: none;
        }
      }
    }
  }
}

#modalRecoverPassword {
  .modal-dialog {
    .modal-content {
      h2 {
        margin-bottom: 15px;
      }
      .button-login {
        margin-top: 25px;
      }
      .support {
        margin-bottom: 50px;
      }
    }
  }
}

@media screen and (max-width: 639px) {
  #modalLogin,
  #modalRecoverPassword {
    .modal-header {
      padding-top: 55px !important;
      h2 {
        font-size: 18px;
      }
    }
    .modal-footer {
      padding-top: 25px !important;
    }
    .button-login {
      font-size: 18px !important;
    }
  }
}
.currency-selector {
  border-radius: 14px;
  width: 100px;
}
.currency-selector:focus {
  box-shadow: none;
  border-color: #f0f0f0;
}
.checkout-checkbox {
  color: $color;
  font-size: 16px;
  padding: 0 15px;
  a {
    color: $link-color !important;
  }
  .custom-checkbox {
    padding-left: 35px;
    .custom-control-input.is-valid ~ .custom-control-label {
      color: $link-color;
    }
    .custom-control-label {
      cursor: pointer;
      &:hover {
        &::before {
          background: #949494;
        }
      }
      &::after,
      &::before {
        border-radius: 0;
        height: 21px;
        left: -35px;
        top: 1px;
        width: 21px;
      }
      &::before {
        background: #d9d9d9;
        border: 0;
      }
    }
    .custom-control-input:checked ~ .custom-control-label::before {
      border-color: #68a432;
      background-color: #68a432;
    }
    .custom-control-input:focus ~ .custom-control-label::before,
    .custom-control-input:focus:not(:checked) ~ .custom-control-label::before {
      border: 0;
      box-shadow: none;
    }
  }
}

.b-toast {
  width: 100%;
  max-width: 100%;
}

#paymentErrorBox {
  width: 100%;
  max-width: 100%;
  border: 0;
  background-color: #ffebeb;
  border-radius: 0;
  box-shadow: none;
  text-align: center;
  font-size: 18px;
  color: #ff0000;
  line-height: 24px;
  padding: 10px 15px;
}

.textGDPR {
  color: $color;
  font-size: 16px;
  margin: 30px 0;
  text-align: center;
}

.support {
  color: #666666;
  font-size: 16px;
  b {
    &:hover {
      color: #666666;
    }
  }
  a {
    color: #666666;
    &:hover {
      color: $link-color;
    }
  }
  &:hover {
    color: #666666;
  }
}

.logo-row {
  margin: 65px 0 0;
  .logoCheckout {
    width: 100%;
    max-width: 228px !important;
    margin: 0 auto;
  }
}

h2.form-header {
  color: $color;
  font-size: 24px;
  font-weight: $font-bold;
  margin-bottom: 15px;
  text-transform: uppercase;
}

.twoColumn {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
}
.button-checkout {
  background: #ff0000;
  color: #ffffff;
  border: none;
  border-radius: 30px;
  width: 100%;
  font-size: 20px;
  text-transform: uppercase;
  padding: 15px;
  line-height: 25px;
}

.button-checkout:hover {
  background: #89c157 !important;
}

.button-checkout:active,
.button-checkout:focus {
  background: #ff0000 !important;
  color: #ffffff !important;
  border-color: #ff0000 !important;
  box-shadow: none !important;
  outline: none;
}
.button-checkout:disabled {
  background: red !important;
  color: white;
}

/* Padding for Stripe Element containers */
.stripe-element-container {
  padding-top: 0.55rem;
  padding-bottom: 0.5rem;
}

/* Blue outline on focus */
.StripeElement--focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

/* Can't see what I type without this */
#card-number.form-control,
#card-cvc.form-control,
#card-exp.form-control {
  display: inline-block;
}
.sr-field-error {
  color: tomato;
}
.spinner,
.spinner:before,
.spinner:after {
  border-radius: 50%;
}
.spinner {
  color: maroon;
  font-size: 22px;
  text-indent: -99999px;
  margin: 0px auto;
  position: relative;
  width: 20px;
  height: 20px;
  box-shadow: inset 0 0 0 2px;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
}
.spinner:before,
.spinner:after {
  position: absolute;
  content: "";
}
.spinner:before {
  width: 10.4px;
  height: 20.4px;
  background: var(--accent-color);
  border-radius: 20.4px 0 0 20.4px;
  top: -0.2px;
  left: -0.2px;
  -webkit-transform-origin: 10.4px 10.2px;
  transform-origin: 10.4px 10.2px;
  -webkit-animation: loading 2s infinite ease 1.5s;
  animation: loading 2s infinite ease 1.5s;
}
.spinner:after {
  width: 10.4px;
  height: 10.2px;
  background: var(--accent-color);
  border-radius: 0 10.2px 10.2px 0;
  top: -0.1px;
  left: 10.2px;
  -webkit-transform-origin: 0px 10.2px;
  transform-origin: 0px 10.2px;
  -webkit-animation: loading 2s infinite ease;
  animation: loading 2s infinite ease;
}

@-webkit-keyframes loading {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes loading {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
.card-label {
  width: 8ch;
  text-align: right;
  font-family: "OpenSans";
}

#upsell-card {
  background-color: rgb(252, 248, 227); //#fffcf4;
  border: 3px dashed black; //1px solid #fce682;
  border-radius: 1px;
}

@media screen and (max-width: 359px) {
  form {
    .radio-products {
      padding: 0;
    }
  }
}

@media screen and (max-width: 767px) {
  form {
    font-size: 16px;
    .input-group {
      input {
        font-size: 16px;
      }
      .custom-control-inline {
        margin: 0;
      }
    }
  }
  .button-checkout {
    font-size: 18px;
  }
  .checkout-checkbox {
    padding: 0;
  }
  .boxCheckout {
    border: 0;
    padding: 0;
  }

  ul.benefits {
    margin: 0 !important;
    padding: 40px 0 0 0;
  }
  ul.benefits li {
    margin: 0;
    font-size: 15px;
    line-height: 40px;
  }
  ul.benefits li svg {
    font-size: 23px;
  }
  .testimonials {
    padding: 20px 25px;
    p {
      font-size: 16px;
      line-height: 25px;
    }
  }
  .textMoneyback {
    font-size: 20px;
  }
  .text-riskFree {
    font-size: 13px;
  }
  .footer {
    padding: 60px 0 40px;
    a {
      font-size: 13px;
      margin: 0 15px;
    }
  }
}

@media screen and (max-width: 1365px) {
  form {
    .card {
      .order-total {
        .vat-text:hover {
          .vat-details {
            right: -10px;
          }
        }
      }
    }
  }
}
</style>
